"use client";

import { Link } from "@tanstack/react-router";
import { useId, useState } from "react";
import { toast } from "sonner";
import { ArrowRight } from "@/components/animate-ui/icons/arrow-right";
import { ResendVerificationButton } from "@/components/resend-verification-button";
import { Button } from "@/components/ui/button";
import { GoogleButton } from "@/components/ui/google-button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useFormValidation } from "@/hooks/use-form-validation";
import { initiateGoogleLogin } from "@/lib/auth/google-sso";
import { login } from "@/lib/auth/service";
import { captureFrontendError } from "@/lib/errors";
import { loginSchema } from "@/lib/validation/auth";

export function LoginForm() {
	const emailId = useId();
	const passwordId = useId();
	const [email, setEmail] = useState("");
	const [password, setPassword] = useState("");
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [showResendVerification, setShowResendVerification] = useState(false);

	// Form validation
	const validation = useFormValidation(loginSchema);

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();

		const formData = {
			email: email.trim(),
			password,
		};

		// Validate form data
		if (!validation.validate(formData)) {
			toast.error("Please fix the errors below");
			return;
		}

		setIsSubmitting(true);
		setShowResendVerification(false); // Reset on new attempt

		try {
			const result = await login(formData);

			if (!result.success) {
				// Set field-level errors from API (422 responses)
				if (result.error.fieldErrors) {
					validation.setServerErrors(result.error.fieldErrors);
				}

				// Show toast for general error
				toast.error(result.error.message);

				// Show resend verification button for 403 errors (unverified email)
				if (result.error.code === "403") {
					setShowResendVerification(true);
				}

				return;
			}

			toast.success("Welcome back!");
			// Navigation handled by useRedirectIfAuthenticated hook in parent route
		} catch (error) {
			captureFrontendError(error, { area: "auth", action: "login_submit" });
			toast.error("An unexpected error occurred. Please try again.");
		} finally {
			setIsSubmitting(false);
		}
	};

	const handleGoogleLogin = async () => {
		try {
			setIsSubmitting(true);
			await initiateGoogleLogin();
			// User will be redirected to Google
		} catch (error) {
			captureFrontendError(error, { area: "auth", action: "login_google_start" });
			toast.error("Failed to start Google sign-in. Please try again.");
			setIsSubmitting(false);
		}
	};

	return (
		<div className="flex flex-col gap-6">
			<div className="flex flex-col gap-2 text-center">
				<h1 className="text-2xl font-semibold tracking-tight">Welcome back</h1>
			</div>

			{/* Google OAuth */}
			<GoogleButton onClick={handleGoogleLogin} disabled={isSubmitting} />

			{/* Divider */}
			<div className="relative">
				<div className="absolute inset-0 flex items-center">
					<span className="w-full border-t" />
				</div>
				<div className="relative flex justify-center text-xs uppercase">
					<span className="bg-background px-2 text-muted-foreground">Or continue with email</span>
				</div>
			</div>

			<form onSubmit={handleSubmit} className="flex flex-col gap-6">
				<div className="flex flex-col gap-4">
					<div className="flex flex-col gap-2">
						<Label htmlFor={emailId}>Email</Label>
						<Input
							id={emailId}
							type="email"
							placeholder="name@example.com"
							value={email}
							onChange={(e) => setEmail(e.target.value)}
							onBlur={() => validation.validateField("email", email, { email, password })}
							required
							autoComplete="email"
							disabled={isSubmitting}
						/>
						{validation.getFieldError("email") && (
							<p className="text-xs text-destructive">{validation.getFieldError("email")}</p>
						)}
					</div>

					<div className="flex flex-col gap-2">
						<div className="flex items-center justify-between">
							<Label htmlFor={passwordId}>Password</Label>
							<Link
								to="/forgot-password"
								className="text-sm text-muted-foreground underline-offset-4 hover:underline"
							>
								Forgot password?
							</Link>
						</div>
						<Input
							id={passwordId}
							type="password"
							value={password}
							onChange={(e) => setPassword(e.target.value)}
							onBlur={() =>
								validation.validateField("password", password, {
									email,
									password,
								})
							}
							required
							autoComplete="current-password"
							disabled={isSubmitting}
						/>
						{validation.getFieldError("password") && (
							<p className="text-xs text-destructive">{validation.getFieldError("password")}</p>
						)}
					</div>
				</div>

				<Button
					type="submit"
					disabled={!email.trim() || !password.trim() || isSubmitting}
					className="w-full"
				>
					{isSubmitting ? (
						"Signing in..."
					) : (
						<>
							Sign in
							<ArrowRight className="ml-2 size-4" />
						</>
					)}
				</Button>
			</form>

			{showResendVerification && email && (
				<div className="rounded-lg border border-yellow-200 bg-yellow-50 p-4 dark:border-yellow-900/50 dark:bg-yellow-950/50">
					<p className="mb-3 text-sm text-yellow-900 dark:text-yellow-100">
						Your email address is not verified. Check your inbox for the verification link or
						request a new one.
					</p>
					<ResendVerificationButton email={email} variant="outline" className="w-full" />
				</div>
			)}

			<div className="text-center text-sm">
				<Link to="/signup" className="text-muted-foreground underline-offset-4 hover:underline">
					Don't have an account? Sign up
				</Link>
			</div>
		</div>
	);
}
